import { useState } from "react";
import { Printer, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatApiError } from "@/lib/api";
import { docsAPI, openPdf, fmtINR, billFileKind, BILL_CATEGORIES, BILL_CATEGORY_LABEL, BILL_CATEGORY_SHORT } from "@/lib/documents";

const CAT_TONE = {
  room: "bg-[#E6FEFF] text-[#014751] border-[#B9EEF1]",
  fnb: "bg-amber-50 text-[#78530E] border-amber-200",
  extras: "bg-violet-50 text-violet-800 border-violet-200",
};

/** Small category chip (read-only). */
export function CategoryBadge({ category, overridden, className = "" }) {
  return (
    <span className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${CAT_TONE[category] || CAT_TONE.extras} ${className}`}
      title={overridden ? "Category manually reassigned" : "Auto-categorised"} data-testid={`category-badge-${category}`}>
      {BILL_CATEGORY_SHORT[category] || category}{overridden && <span aria-hidden>*</span>}
    </span>
  );
}

/** Reassign a folio charge line to Room / F&B / Extras. Calls PATCH /bills/line-category then onChanged(result). */
export function CategorySelect({ stayId, line, disabled, onChanged, className = "" }) {
  const [busy, setBusy] = useState(false);
  const change = async (v) => {
    if (v === line.bill_category) return;
    setBusy(true);
    try {
      const res = await docsAPI.setLineCategory(stayId, line.ref, v);
      toast.success(`Moved to ${BILL_CATEGORY_LABEL[v]}`);
      onChanged?.(res);
    } catch (e) { toast.error(formatApiError(e) || "Could not change category"); }
    finally { setBusy(false); }
  };
  if (disabled) return <CategoryBadge category={line.bill_category} overridden={line.category_overridden} className={className} />;
  return (
    <Select value={line.bill_category} onValueChange={change} disabled={busy}>
      <SelectTrigger className={`h-6 w-[108px] px-1.5 text-[10px] font-semibold uppercase tracking-wider border ${CAT_TONE[line.bill_category] || ""} ${className}`}
        data-testid={`category-select-${line.ref}`} title={line.category_overridden ? "Manually reassigned" : "Auto-categorised — change if needed"}>
        {busy ? <Loader2 size={11} className="animate-spin" /> : <SelectValue />}
      </SelectTrigger>
      <SelectContent>{BILL_CATEGORIES.map(([k, l]) => <SelectItem key={k} value={k} className="text-[12px]">{l}</SelectItem>)}</SelectContent>
    </Select>
  );
}

/** Per-category totals with the combined grand total. `categories` from folio-summary or bill.category_sections. */
export function CategoryTotals({ categories = [], grandTotal, className = "", rowClass = "flex justify-between", testid = "category-totals" }) {
  return (
    <div className={className} data-testid={testid}>
      {categories.map((c) => (
        <div key={c.key} className={`${rowClass} text-[12.5px]`} data-testid={`category-total-${c.key}`}>
          <span className="flex items-center gap-2">{c.label || BILL_CATEGORY_LABEL[c.key]}{c.count === 0 && <span className="text-[10.5px] text-muted-foreground">(none)</span>}</span>
          <span className="mono">{fmtINR(c.total || 0)}</span>
        </div>
      ))}
      {grandTotal !== undefined && (
        <div className={`${rowClass} border-t border-hocp-border pt-1.5 mt-1 font-semibold text-[13px]`} data-testid="category-grand-total">
          <span>All charges</span><span className="mono">{fmtINR(grandTotal)}</span>
        </div>
      )}
    </div>
  );
}

/** Print / download an individual category bill (Room · F&B · Extras). Empty categories are disabled. */
export function CategoryBillButtons({ bill, size = "sm", className = "" }) {
  if (!bill) return null;
  const sections = bill.category_sections || [];
  const has = (k) => (sections.find((s) => s.key === k)?.lines?.length || 0) > 0;
  const cls = size === "sm" ? "h-7 px-2 text-[11px]" : "h-9 px-3 text-[12px]";
  return (
    <div className={`inline-flex flex-wrap items-center gap-1 ${className}`} data-testid={`category-bill-buttons-${bill.id}`}>
      <span className="text-[10px] mono uppercase tracking-wider text-muted-foreground mr-0.5">Split:</span>
      {BILL_CATEGORIES.map(([k, l]) => (
        <button key={k} type="button" disabled={!has(k)} title={has(k) ? `Print ${l} bill` : `No ${l.toLowerCase()} on this bill`}
          onClick={() => openPdf(docsAPI.billPdfUrl(bill.id, k), `${billFileKind(bill)}-${bill.bill_number}-${BILL_CATEGORY_SHORT[k].replace("&", "")}.pdf`)}
          className={`inline-flex items-center gap-1 rounded-md border ${cls} font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${CAT_TONE[k]} hover:brightness-95`}
          data-testid={`print-category-${k}-${bill.id}`}>
          <Printer size={11} />{BILL_CATEGORY_SHORT[k]}
        </button>
      ))}
    </div>
  );
}
